<script setup lang="ts">
import { AisClearRefinements, AisStateResults, AisRangeInput, AisToggleRefinement, AisDynamicWidgets } from 'vue-instantsearch/vue3/es'
import { vOnClickOutside } from '@vueuse/components'
import { useResizeObserver } from '@vueuse/core'

const { t } = useI18n()
const { getFormattedPrice, currencyCode } = usePrice()
const sidenav = useSidenav()
const { sessionContext } = useSessionContext()

// Mirrors the result cards' price selection in mapSearchApiItems: price.gross for
// displayGross (B2C) customer groups, price.net otherwise.
const displayGross = computed(() => sessionContext.value?.currentCustomerGroup?.displayGross !== false)
const priceAttribute = computed(() => displayGross.value ? 'price.gross' : 'price.net')
const facetAttributes = computed(() => [priceAttribute.value, 'manufacturer', 'color.name', 'onSale'])

const filterInner = ref<HTMLElement | null>(null)
const filterOuter = ref<HTMLElement | null>(null)
const showMoreFilter = ref(false)
const showAllFilter = ref(false)
const dropdownTrigger = ref<string | false>(false)
const priceInputMinIsTouched = ref(false)
const priceInputMaxIsTouched = ref(false)

// While a range-slider thumb is being dragged, `currentRefinement` from ais-range-input
// only updates on release (its `change` event) — these track the in-progress value so the
// displayed price follows the thumb instead of jumping only when the user lets go.
const draggingMin = ref<number | null>(null)
const draggingMax = ref<number | null>(null)

function effectiveMin(currentRefinement: { min?: number }, range: { min: number }): number {
    return draggingMin.value ?? (currentRefinement.min != null ? currentRefinement.min : range.min)
}

function effectiveMax(currentRefinement: { max?: number }, range: { max: number }): number {
    return draggingMax.value ?? (currentRefinement.max != null ? currentRefinement.max : range.max)
}

function isPriceRefined(currentRefinement: { min?: number, max?: number }, range: { min: number, max: number }): boolean {
    return effectiveMin(currentRefinement, range) > range.min || effectiveMax(currentRefinement, range) < range.max
}

useResizeObserver(filterOuter, () => {
    if (filterInner.value?.offsetWidth != null && filterOuter.value?.offsetWidth) {
        showMoreFilter.value = filterInner.value.offsetWidth > filterOuter.value.offsetWidth
    }
})

// The A4G Search API returns no renderingContent.facetOrdering (server-driven facet
// ordering is not used, see ticket #89), and ais-dynamic-widgets' default attributesToRender
// falls back to an empty array in that case — so this fixed list is the only source of
// truth for which facets render and in what order.
//
// The widgets below are declared statically rather than looped over a config array:
// ais-dynamic-widgets discovers a widget's facet attribute by reading each direct slot
// child's `attribute` prop, but any v-for compiles to a single Fragment vnode as that
// slot's whole content, which it can't see into — so a looped list of widgets here
// silently renders nothing.
//
// ais-dynamic-widgets is bound with `:facets="[]"` (not this list): its own
// getWidgetSearchParameters() would otherwise register every attribute here as a
// *conjunctive* facet before each widget below gets to configure its own attribute —
// connectToggleRefinement specifically skips its disjunctive-facet setup when the
// attribute is already conjunctive, so the toggle's helper.addDisjunctiveFacetRefinement()
// call throws ("onSale is not defined in the disjunctiveFacets attribute of the helper
// configuration") before a search ever fires. `facets` only matters for Algolia's
// server-driven facet ordering, which isn't used here, so suppressing it is safe.
function fixedFacetOrder(): string[] {
    return facetAttributes.value
}

function showFilterDropdown(trigger: string) {
    dropdownTrigger.value = dropdownTrigger.value === trigger ? false : trigger
}

function setShowAllFilter() {
    showMoreFilter.value = false
    showAllFilter.value = true
}

function hideFilterDropdown(id: string) {
    if (dropdownTrigger.value === id) {
        dropdownTrigger.value = false
    }
}
</script>

<template>
    <div>
        <div ref="filterOuter" class="relative w-full max-w-full">
            <div
                ref="filterInner"
                class="w-full relative md:inline-flex md:items-start md:gap-2 md:w-max"
                :class="showAllFilter ? 'md:flex-wrap md:max-w-full' : 'md:flex-nowrap'"
            >
                <ais-dynamic-widgets class="contents" :facets="[]" :transform-items="fixedFacetOrder">
                    <ais-range-input
                        :attribute="priceAttribute"
                        :class-names="{
                            'ais-RangeInput--noRefinement': 'hidden'
                        }"
                    >
                        <template
                            #default="{
                                currentRefinement,
                                range,
                                canRefine,
                                refine: refineRange
                            }"
                        >
                            <div
                                v-show="range.min !== range.max"
                                v-on-click-outside="() => hideFilterDropdown('price.net')"
                                class="filter-dropdown relative shrink-0"
                            >
                                <button
                                    tabindex="0"
                                    class="group w-full flex justify-between items-center font-semibold min-h-11 py-3 pl-4 pr-1.5 border-b md:py-0 md:w-auto md:btn md:btn-filter md:gap-1 md:pr-3 md:pl-[14px] bg-base-100"
                                    :class="{
                                        'md:btn-filter-set': isPriceRefined(currentRefinement, range),
                                        'bg-base-200': dropdownTrigger === 'price.net'
                                    }"
                                    @click="showFilterDropdown('price.net')"
                                >
                                    <span class="flex items-center min-h-11 md:block md:min-h-fit">
                                        {{ t('search.filter.priceLabel') }}
                                    </span>

                                    <span class="flex gap-3 items-center md:gap-1">
                                        <span
                                            v-if="isPriceRefined(currentRefinement, range)"
                                            class="badge badge-sm badge-dark"
                                        >
                                            <template v-if="effectiveMin(currentRefinement, range) > range.min">
                                                {{ getFormattedPrice(effectiveMin(currentRefinement, range)) }}
                                            </template>
                                            <template v-else>
                                                {{ getFormattedPrice(range.min) }}
                                            </template>
                                            -
                                            <template v-if="effectiveMax(currentRefinement, range) < range.max">
                                                {{ getFormattedPrice(effectiveMax(currentRefinement, range)) }}
                                            </template>
                                            <template v-else>
                                                {{ getFormattedPrice(range.max) }}
                                            </template>
                                        </span>

                                        <span
                                            class="w-11 h-11 rounded-full items-center flex justify-center hover:bg-tertiary-focus md:h-auto md:w-auto md:hover:bg-transparent"
                                            :class="{ 'bg-tertiary-focus md:bg-transparent rotate-180' : dropdownTrigger === 'price.net'}"
                                        >
                                            <MiscIcon name="chevron-down" />
                                        </span>
                                    </span>
                                </button>

                                <div
                                    v-if="dropdownTrigger === 'price.net'"
                                    tabindex="0"
                                    class="filter-dropdown-content max-h-[400px] xl:max-h-[450px] gap-3 p-4 overflow-y-auto flex flex-col bg-base-100 z-20 border-b md:absolute md:shadow md:rounded-box md:border md:w-80 md:translate-y-3"
                                >
                                    <div class="range-slider">
                                        <input
                                            type="range"
                                            class="range-slider-filter"
                                            :min="range.min"
                                            :max="range.max"
                                            :placeholder="range.min"
                                            :disabled="!canRefine"
                                            :value="effectiveMin(currentRefinement, range)"
                                            :data-insights-filter="`${priceAttribute}:${effectiveMin(currentRefinement, range)}`"
                                            :aria-label="t('search.filter.priceLabel')"
                                            @input="draggingMin = Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number)"
                                            @change="refineRange({
                                                min: Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number),
                                                max: effectiveMax(currentRefinement, range),
                                            }); draggingMin = null"
                                        >
                                        <input
                                            type="range"
                                            class="range-slider-filter"
                                            :min="range.min"
                                            :max="range.max"
                                            :placeholder="range.max"
                                            :disabled="!canRefine"
                                            :value="effectiveMax(currentRefinement, range)"
                                            :data-insights-filter="`${priceAttribute}:${effectiveMax(currentRefinement, range)}`"
                                            :aria-label="t('search.filter.priceLabel')"
                                            @input="draggingMax = Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number)"
                                            @change="refineRange({
                                                min: effectiveMin(currentRefinement, range),
                                                max: Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number),
                                            }); draggingMax = null"
                                        >
                                    </div>

                                    <div class="label flex">
                                        <div class="label-text-alt text-right bg-base-250 py-1.5 px-3 w-full">
                                            <label for="price.net" class="flex justify-center w-full">
                                                <input
                                                    :class="{'text-neutral' : priceInputMinIsTouched}"
                                                    type="number"
                                                    class="bg-base-250 text-right w-1/2 label-text-alt"
                                                    :min="range.min"
                                                    :max="range.max"
                                                    :value="effectiveMin(currentRefinement, range)"
                                                    @blur="priceInputMinIsTouched = true"
                                                    @change="refineRange({
                                                        min: Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number),
                                                        max: effectiveMax(currentRefinement, range),
                                                    })"
                                                >
                                                {{ currencyCode }}
                                            </label>
                                        </div>
                                        <span class="px-0.5">-</span>
                                        <div class="label-text-alt text-right bg-base-250 py-1.5 px-3 w-full">
                                            <label class="flex justify-center w-full">
                                                <input
                                                    :class="{'text-neutral' : priceInputMaxIsTouched}"
                                                    type="number"
                                                    class="bg-base-250 text-right w-1/2 label-text-alt"
                                                    :min="range.min"
                                                    :max="range.max"
                                                    :value="effectiveMax(currentRefinement, range)"
                                                    @blur="priceInputMaxIsTouched = true"
                                                    @change="refineRange({
                                                        min: effectiveMin(currentRefinement, range),
                                                        max: Math.floor(($event.currentTarget as HTMLInputElement).value as unknown as number),
                                                    })"
                                                >
                                                {{ currencyCode }}
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="isPriceRefined(currentRefinement, range)" class="flex justify-end">
                                        <button
                                            type="button"
                                            class="font-normal text-xs underline shrink-0 cursor-pointer"
                                            @click="draggingMin = null; draggingMax = null; refineRange({
                                                min: range.min,
                                                max: range.max,
                                            })"
                                        >
                                            {{ $t('product.listing.filter.reset') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </ais-range-input>

                    <SearchFilterMeiliRefinementList
                        attribute="manufacturer"
                        :label="t('search.filter.brandLabel')"
                        :is-open="dropdownTrigger === 'manufacturer'"
                        @toggle="showFilterDropdown('manufacturer')"
                        @close="hideFilterDropdown('manufacturer')"
                    />

                    <SearchFilterMeiliRefinementList
                        attribute="color.name"
                        :label="t('search.filter.colorLabel')"
                        :is-open="dropdownTrigger === 'color.name'"
                        display-type="color"
                        @toggle="showFilterDropdown('color.name')"
                        @close="hideFilterDropdown('color.name')"
                    />

                    <ais-toggle-refinement
                        attribute="onSale"
                        :label="t('search.filter.forSale')"
                        :class-names="{
                            'ais-ToggleRefinement--noRefinement': 'hidden'
                        }"
                    >
                        <template #default="{ value, refine }">
                            <div class="shrink-0 last-of-type:dropdown-bottom last-of-type:dropdown-end">
                                <label
                                    for="onSale"
                                    class="w-full flex justify-between items-center font-semibold min-h-11 py-3 pl-4 pr-1.5 border-b md:py-0 md:w-auto md:btn md:btn-filter md:gap-1 md:pr-3 md:pl-[14px] bg-base-100"
                                >
                                    <span class="flex items-center min-h-11 md:block md:min-h-fit">{{ t('search.filter.forSale') }}</span>
                                    <input
                                        id="onSale"
                                        type="checkbox"
                                        :checked="value.isRefined"
                                        class="toggle"
                                        :data-insights-filter="`onSale:${value.isRefined}`"
                                        @change="refine(value)"
                                    >
                                </label>
                            </div>
                        </template>
                    </ais-toggle-refinement>
                </ais-dynamic-widgets>

                <ais-state-results class="block md:hidden sticky bottom-0 w-full z-50 bg-base-100 p-4 border-t">
                    <template #default="{ nbHits }">
                        <div>
                            <button
                                type="button"
                                class="btn btn-secondary w-full"
                                @click="sidenav.toggle('searchFilter')"
                                @keydown.enter.prevent="sidenav.toggle('searchFilter')"
                            >
                                {{ $t('product.listing.filter.results', { count: nbHits }) }}
                            </button>
                        </div>
                    </template>
                </ais-state-results>

                <ais-clear-refinements>
                    <template #default="{ canRefine, refine }">
                        <button
                            v-show="canRefine"
                            type="button"
                            class="btn btn-sm btn-link rounded-full text-xs text-base-content-subtle hidden md:inline-flex"
                            @click="refine"
                        >
                            {{ $t('product.listing.filter.resetFilter') }}
                        </button>
                    </template>
                </ais-clear-refinements>
            </div>

            <div v-if="showMoreFilter" class="absolute overflow-hidden right-0 top-0 bg-white shadow-[-20px_0px_10px_10px_rgba(255,255,255)] z-10">
                <button type="button" class="btn btn-ghost btn-icon-right" @click="setShowAllFilter()">
                    {{ $t('product.listing.filter.moreFilter') }}
                    <MiscIcon name="chevron-down" />
                </button>
            </div>
        </div>
    </div>
</template>
